'use client';

import { getCookieValue, setCookie } from '@/utils/cookie';
import { newDate } from '@/utils/date';
import { differenceInSeconds } from 'date-fns';
import { notFound } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * 別ページリダイレクト条件をチェックし、条件に合致すればリダイレクトする
 * @returns リダイレクトした場合はtrue、しなかった場合はfalse
 */
const checkAndRedirectToOtherPage = (lotteryId: number, isXRequestedWithMatched: boolean): boolean => {
  // x-requested-withが環境変数と一致する場合はリダイレクトをスキップ
  if (isXRequestedWithMatched) {
    return false;
  }

  const otherPageRedirectLotteryId = process.env.NEXT_PUBLIC_OTHER_PAGE_REDIRECT_LOTTERY_ID;
  const otherPageRedirectUrl = process.env.NEXT_PUBLIC_OTHER_PAGE_REDIRECT_URL;

  if (otherPageRedirectLotteryId && otherPageRedirectUrl && String(lotteryId) === otherPageRedirectLotteryId) {
    window.location.href = otherPageRedirectUrl;
    return true;
  }
  return false;
};

// リファラ制限してる場合の処理コンポーネント
function CheckReferrer({
  approveReferrer,
  lotteryId,
  startAt,
  endAt,
  isPreview = false,
  referrer,
  isXRequestedWithMatched = false,
}: {
  approveReferrer?: string;
  lotteryId: number;
  startAt: string;
  endAt?: string;
  isPreview?: boolean;
  referrer?: string;
  isXRequestedWithMatched?: boolean;
}) {
  const checkingRef = useRef(false);
  const cookieKey = `approved_referrer_${lotteryId}`;
  useEffect(() => {
    // プレビュー時は無条件で通す
    if (isPreview) return;
    if (checkingRef.current) {
      return;
    }
    checkingRef.current = true;
    if (getCookieValue(cookieKey) === 'true') return;

    // x-requested-withヘッダーが環境変数と一致する場合はアクセスを許可
    // NEXT_PUBLIC_OTHER_PAGE_REDIRECT_LOTTERY_IDに該当するくじでも許可
    // サーバーサイドで比較済みの結果を使用
    if (isXRequestedWithMatched) {
      // リロード対策にクッキーをセット
      const hasPeriod = endAt && startAt;
      const maxAge = hasPeriod ? differenceInSeconds(newDate(endAt), newDate(startAt)) : 0;
      setCookie(cookieKey, 'true', maxAge);
      return;
    }

    // そもそもリファラ制限をしてない場合
    // x-requested-withが一致しない場合のみ、approveReferrerのチェックを行う
    if (!approveReferrer) return;

    if (!referrer) {
      // 別ページリダイレクト条件をチェック
      if (checkAndRedirectToOtherPage(lotteryId, isXRequestedWithMatched)) return;
      notFound();
      return;
    }

    try {
      // ドメインベースのチェックに変更
      const approveReferrerUrl = new URL(approveReferrer.trim());
      const referrerUrl = new URL(referrer.trim());

      if (approveReferrerUrl.hostname !== referrerUrl.hostname) {
        // 別ページリダイレクト条件をチェック
        if (checkAndRedirectToOtherPage(lotteryId, isXRequestedWithMatched)) return;
        notFound();
        return;
      }
    } catch {
      // URLのパースに失敗した場合
      // 別ページリダイレクト条件をチェック
      if (checkAndRedirectToOtherPage(lotteryId, isXRequestedWithMatched)) return;
      notFound();
      return;
    }

    // リロード対策にクッキーをセット
    // maxAgeはくじ開催期間と同じ
    const hasPeriod = endAt && startAt;
    const maxAge = hasPeriod ? differenceInSeconds(newDate(endAt), newDate(startAt)) : 0;
    setCookie(cookieKey, 'true', maxAge);
  }, []);
  return <></>;
}

export default CheckReferrer;
